import React from 'react';
import { useSortable, defaultAnimateLayoutChanges } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useContextMenu } from 'react-contexify';
import { MENU_ID } from '../constants';
import { formatKgFR } from '../utils/formatters';
import { useDoubleTap } from '../hooks/useDoubleTap';

export function SortableItem({ item, container, canEdit, departureTotalKg, setCtxMenu }) {
  const { show } = useContextMenu({ id: MENU_ID });
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: String(item.id),
    data: { container },
    animateLayoutChanges: defaultAnimateLayoutChanges
  });

  const showContextMenu = React.useCallback((e) => {
    if (!canEdit) return;

    setCtxMenu?.({
      id: item.id,
      isDeparture: item.type === 'departure',
      isTask: item.type !== 'departure',
      showLoadKg: item.showLoadKg ?? true,
      dateADate: item.dateADate ?? false,
      reprise: item.reprise ?? 0,
      cornerTag: item.cornerTag ?? ''
    });

    show({
      event: e.nativeEvent || e,
      props: {
        id: item.id,
        isDeparture: item.type === 'departure',
        showLoadKg: item.showLoadKg ?? true
      }
    });
  }, [canEdit, item, setCtxMenu, show]);

  const doubleTapHandlers = useDoubleTap(showContextMenu);

  const isTemporary = item.isTemporary === true;
  const isDeparture = item.type === 'departure';

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    touchAction: canEdit ? 'none' : undefined,
    padding: '12px',
    marginBottom: '8px',
    background: isDeparture
      ? '#e6f0ff'                 // 🔵 bleu clair
      : isTemporary
      ? '#fefcea'
      : '#fff',
    border: isDeparture
      ? '2px solid #4a90e2'       // 🔵 bordure bleue
      : isTemporary
      ? '2px dashed orange'
      : '1px solid #ccc',
    borderRadius: '4px',
    userSelect: 'none',
    position: 'relative',
    fontWeight: isDeparture ? '600' : 'normal'
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...(canEdit ? listeners : {})}

      onContextMenu={(e) => {
        e.preventDefault();
        showContextMenu(e);
      }}
      {...doubleTapHandlers}
    >
      {item.cornerTag && (
        <div className="corner-tag-wrapper">
          <div className="corner-tag">({item.cornerTag})</div>
        </div>
      )}

      <div>
        <strong>
          {isDeparture ? (
            <>
              ⏱️ {item.order_number}
              {(item.showLoadKg ?? true) && typeof departureTotalKg === 'number' && (
                <span style={{ marginLeft: 8, fontWeight: 800 }}>
                  · {formatKgFR(departureTotalKg)}kg
                </span>
              )}
            </>
          ) : (
            `#${item.order_number}`
          )}
        </strong>
      </div>
      {item.type !== 'departure' && (
        <div>
          {item.city} ({item.weight}kg)
        </div>
      )}
      {item.note && (
        <div style={{ fontSize: '0.9em', marginTop: '4px' }}>{item.note}</div>
      )}
      {item.reprise > 0 && (
        <div className="bottom-tag-reprise">Reprise</div>
      )}
      {item.dateADate && (
        <div className="bottom-tag-date">date à date</div>
      )}
      {isTemporary && (
        <span
          style={{
            position: 'absolute',
            top: -10,
            right: -10,
            background: 'orange',
            color: '#fff',
            fontSize: '10px',
            padding: '2px 6px',
            borderRadius: '8px',
            fontWeight: 'bold'
          }}
        >
          non sauvegardé
        </span>
      )}
    </div>
  );
}
